import { throttle } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { presenceService } from '../realtime';
import { useAppDispatch, useAppSelector } from '../store';
import { ActiveUser, getActiveUsers } from '../store/siteInfoSlice';
import { getCursorsVisible, getInputValue, getShowTempMessage, setInputValue, setShowTempMessage } from '../store/uiSlice';
import { getLoggedIn, getUser } from '../store/userSlice';
import getUniqueSelector from '../utils/selector';
import useGetPageId from './use-get-pageid';

export interface UserPointer {
  relativeX: number;
  relativeY: number;
  absX?: number; // absolute document X (pageX)
  absY?: number; // absolute document Y (pageY)
  docRelX?: number; // absolute normalized by document width [0,1]
  docRelY?: number; // absolute normalized by document height [0,1]
  scrollRelX?: number; // scroll progress X normalized by (docWidth - viewportWidth)
  scrollRelY?: number; // scroll progress Y normalized by (docHeight - viewportHeight)
  anchor?: ContentAnchor;
  user: ActiveUser;
  timestamp: number;
  message?: string;
}

export interface ContentAnchor {
  type: 'id' | 'path';
  value: string; // element id or CSS path
  offsetX: number; // within element bounding rect [0,1]
  offsetY: number; // within element bounding rect [0,1]
}

export interface PointerData {
  x: number;
  y: number;
  title: string;
  src: string | null;
  userId: string;
  message?: string;
}

// Whisper data types
interface MouseMoveWhisperData {
  userId: string;
  user: ActiveUser;
  relativeX: number;
  relativeY: number;
  absX: number;
  absY: number;
  docRelX: number;
  docRelY: number;
  scrollRelX: number;
  scrollRelY: number;
  anchor?: ContentAnchor;
  timestamp: number;
  message: string;
}

interface MouseLeaveWhisperData {
  userId: string;
  timestamp: number;
}

interface MessageWhisperData {
  userId: string;
  message: string;
  relativeX: number;
  relativeY: number;
  absX: number;
  absY: number;
  docRelX: number;
  docRelY: number;
  scrollRelX: number;
  scrollRelY: number;
  anchor?: ContentAnchor;
  user: ActiveUser;
  timestamp: number;
}

interface CancelMessageWhisperData {
  userId: string;
  timestamp: number;
}

interface UseCollaborativePointersProps {
  enabled?: boolean;
}

interface UseCollaborativePointersReturn {
  pointers: PointerData[];
  handleMouseMove: (e: MouseEvent) => void;
  handleMouseLeave: () => void;
  handleMouseEnter: () => void;
  handleEscapeMessage: (event: KeyboardEvent) => void;
}

export const useCollaborativePointers = ({
  enabled = true
}: UseCollaborativePointersProps): UseCollaborativePointersReturn => {
  const { pageId } = useGetPageId()
  const currentUser = useAppSelector(getUser);
  const isLoggedIn = useAppSelector(getLoggedIn);
  const activeUsers = useAppSelector(getActiveUsers);
  const cursorsVisible = useAppSelector(getCursorsVisible);
  const [userPointers, setUserPointers] = useState<Map<string, UserPointer>>(new Map());
  const [hasLeftPage, setHasLeftPage] = useState(false);
  const showTempMessage = useAppSelector(getShowTempMessage);
  const inputValue = useAppSelector(getInputValue);
  const dispatch = useAppDispatch();

  // Track current relative mouse position for temp message broadcasting
  const currentRelativePosition = useRef({ relativeX: 0, relativeY: 0 });

  const hideMessageTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const clearMessageTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Generate a unique subscriber ID for this hook instance
  const subscriberIdRef = useRef(`collaborative-pointers-${Math.random().toString(36).substr(2, 9)}`);
  const subscriberId = subscriberIdRef.current;

  // Only create channel when we have a valid siteId
  const shouldConnect = Boolean(pageId && enabled);
  const channelName = shouldConnect ? `pages.${pageId}` : null;
  // Viewport-relative utilities
  const getViewportSize = useCallback(() => {
    return { width: window.innerWidth || 1, height: window.innerHeight || 1 };
  }, []);

  // Document size utilities (content/canvas space)
  const getDocumentSize = useCallback(() => {
    const doc = document.documentElement;
    const body = document.body;
    const width = Math.max(
      doc.scrollWidth,
      doc.offsetWidth,
      doc.clientWidth,
      body?.scrollWidth || 0,
      body?.offsetWidth || 0
    );
    const height = Math.max(
      doc.scrollHeight,
      doc.offsetHeight,
      doc.clientHeight,
      body?.scrollHeight || 0,
      body?.offsetHeight || 0
    );
    return { width, height };
  }, []);

  // Scroll extents based solely on scrollable content size minus viewport
  const getScrollableExtents = useCallback(() => {
    const doc = document.documentElement;
    const body = document.body;
    const contentWidth = Math.max(doc.scrollWidth, body?.scrollWidth || 0);
    const contentHeight = Math.max(doc.scrollHeight, body?.scrollHeight || 0);
    const vw = window.innerWidth || doc.clientWidth || 0;
    const vh = window.innerHeight || doc.clientHeight || 0;
    const maxScrollX = Math.max(0, contentWidth - vw);
    const maxScrollY = Math.max(0, contentHeight - vh);
    return { maxScrollX, maxScrollY };
  }, []);

  // Track current absolute mouse position for temp message broadcasting
  const currentAbsolutePosition = useRef({ absX: 0, absY: 0 });
  // Track current document-relative position for temp message broadcasting
  const currentDocRelativePosition = useRef({ docRelX: 0, docRelY: 0 });
  // Track current scroll-relative position for temp message broadcasting
  const currentScrollRelative = useRef({ scrollRelX: 0, scrollRelY: 0 });
  // Track current content anchor for temp message broadcasting
  const currentAnchorRef = useRef<ContentAnchor | undefined>(undefined);

  const getAnchorAtPoint = useCallback((clientX: number, clientY: number): ContentAnchor | undefined => {
    const el = document.elementFromPoint(clientX, clientY) as HTMLElement | null;
    if (!el) return undefined;
    // walk up a few ancestors to find an element with a stable id
    let cur: HTMLElement | null = el;
    for (let depth = 0; depth < 6 && cur; depth += 1) {
      if (cur.id && document.getElementById(cur.id) === cur) {
        const rect = cur.getBoundingClientRect();
        const scrollX = window.scrollX || window.pageXOffset || 0;
        const scrollY = window.scrollY || window.pageYOffset || 0;
        const absLeft = rect.left + scrollX;
        const absTop = rect.top + scrollY;
        const absX = clientX + scrollX;
        const absY = clientY + scrollY;
        const width = Math.max(1, rect.width);
        const height = Math.max(1, rect.height);
        const offsetX = Math.max(0, Math.min(1, (absX - absLeft) / width));
        const offsetY = Math.max(0, Math.min(1, (absY - absTop) / height));
        return { type: 'id', value: cur.id, offsetX, offsetY };
      }
      cur = cur.parentElement;
    }
    // Fallback: build a DOM path for the original element
    const target: HTMLElement = el;
    const path = getUniqueSelector(target);
    if (path) {
      try {
        const found = document.querySelector(path);
        if (found) {
          const rect = target.getBoundingClientRect();
          const scrollX = window.scrollX || window.pageXOffset || 0;
          const scrollY = window.scrollY || window.pageYOffset || 0;
          const absLeft = rect.left + scrollX;
          const absTop = rect.top + scrollY;
          const absX = clientX + scrollX;
          const absY = clientY + scrollY;
          const width = Math.max(1, rect.width);
          const height = Math.max(1, rect.height);
          const offsetX = Math.max(0, Math.min(1, (absX - absLeft) / width));
          const offsetY = Math.max(0, Math.min(1, (absY - absTop) / height));
          return { type: 'path', value: path, offsetX, offsetY };
        }
      } catch (_e) {
        // ignore invalid selector
      }
    }
    return undefined;
  }, []);

  const getPresenceChannel = useCallback(() => {
    if (!channelName || !isLoggedIn) return null;
    return presenceService.register(channelName, subscriberId);
  }, [channelName, isLoggedIn, subscriberId]);

  const throttledBroadcastMouseMove = useMemo(() =>
    throttle((
      relativeX: number,
      relativeY: number,
      absX: number,
      absY: number,
      docRelX: number,
      docRelY: number,
      scrollRelX: number,
      scrollRelY: number,
      anchor: ContentAnchor | undefined,
      user: ActiveUser,
      message: string
    ) => {
      if (!enabled || !cursorsVisible || hasLeftPage || !user) return;
      const presenceChannel = getPresenceChannel();
      if (presenceChannel) {
        presenceChannel.whisper('mouse-move', {
          userId: user.id,
          user,
          relativeX,
          relativeY,
          absX,
          absY,
          docRelX,
          docRelY,
          scrollRelX,
          scrollRelY,
          anchor,
          timestamp: Date.now(),
          message: message.trim(),
        });
      }
    }, 200)
    , [enabled, cursorsVisible, hasLeftPage, getPresenceChannel]);

  const broadcastMouseLeave = useCallback(() => {
    // Don't send leave updates if cursors are hidden or disabled
    if (!enabled || !cursorsVisible || !currentUser) return;

    setHasLeftPage(true);

    const presenceChannel = getPresenceChannel();
    if (presenceChannel) {
      presenceChannel.whisper('mouse-leave', {
        userId: currentUser.id,
        timestamp: Date.now()
      });
    }

    throttledBroadcastMouseMove.cancel();
  }, [currentUser, enabled, cursorsVisible, throttledBroadcastMouseMove, getPresenceChannel]);

  const handleMouseEnter = useCallback(() => {
    if (!enabled) return;
    setHasLeftPage(false);
  }, [enabled]);

  // Send both absolute document coords and viewport-relative coords
  const handleMouseMove = useCallback((e: MouseEvent) => {
    // Don't handle mouse move if cursors are hidden or disabled
    if (!enabled || !cursorsVisible || !currentUser || hasLeftPage) return;

    // Use clientX/clientY for viewport coordinates, then convert to relative [0,1]
    const { width, height } = getViewportSize();
    const relativeX = Math.max(0, Math.min(1, e.clientX / width));
    const relativeY = Math.max(0, Math.min(1, e.clientY / height));

    // Absolute document coordinates
    const absX = e.pageX;
    const absY = e.pageY;

    // Document-relative normalized coordinates
    const doc = getDocumentSize();
    const docRelX = Math.max(0, Math.min(1, absX / (doc.width || 1)));
    const docRelY = Math.max(0, Math.min(1, absY / (doc.height || 1)));

    // Scroll-relative normalized coordinates (progress of viewport top)
    const scrollX = window.scrollX || window.pageXOffset || 0;
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const { maxScrollX, maxScrollY } = getScrollableExtents();
    const scrollRelX = maxScrollX > 0 ? Math.max(0, Math.min(1, scrollX / maxScrollX)) : 0;
    const scrollRelY = maxScrollY > 0 ? Math.max(0, Math.min(1, scrollY / maxScrollY)) : 0;

    // Optional content anchor
    const anchor = getAnchorAtPoint(e.clientX, e.clientY);
    currentAnchorRef.current = anchor;

    // Store current relative position for temp messages
    currentRelativePosition.current = { relativeX, relativeY };
    // Store current absolute position for temp messages
    currentAbsolutePosition.current = { absX, absY };
    // Store current document-relative position for temp messages
    currentDocRelativePosition.current = { docRelX, docRelY };
    // Store current scroll-relative position for temp messages
    currentScrollRelative.current = { scrollRelX, scrollRelY };

    const user = activeUsers[currentUser.id];
    throttledBroadcastMouseMove(
      relativeX,
      relativeY,
      absX,
      absY,
      docRelX,
      docRelY,
      scrollRelX,
      scrollRelY,
      anchor,
      user,
      inputValue
    );
  }, [activeUsers, throttledBroadcastMouseMove, currentUser, enabled, cursorsVisible, hasLeftPage, getViewportSize, getDocumentSize, inputValue, getAnchorAtPoint, getScrollableExtents]);

  // Map incoming pointer to local viewport coordinates
  const calculateLocalPosition = useCallback((pointer: UserPointer): { x: number; y: number } => {
    const scrollX = window.scrollX || window.pageXOffset || 0;
    const scrollY = window.scrollY || window.pageYOffset || 0;

    // Highest priority: anchor mapping to the same element when available
    if (pointer.anchor) {
      const target = pointer.anchor.type === 'id'
        ? document.getElementById(pointer.anchor.value)
        : document.querySelector(pointer.anchor.value);
      if (target) {
        const rect = target.getBoundingClientRect();
        const x = rect.left + pointer.anchor.offsetX * Math.max(1, rect.width);
        const y = rect.top + pointer.anchor.offsetY * Math.max(1, rect.height);
        return { x, y };
      }
    }

    // Next: scroll-progress + viewport-relative mapping (captures sender's viewport position)
    if (typeof pointer.scrollRelY === 'number' && typeof pointer.scrollRelX === 'number') {
      const { width: vw, height: vh } = getViewportSize();
      const { maxScrollX, maxScrollY } = getScrollableExtents();

      const localAbsX = pointer.scrollRelX * maxScrollX + pointer.relativeX * vw;
      const localAbsY = pointer.scrollRelY * maxScrollY + pointer.relativeY * vh;

      const x = localAbsX - scrollX;
      const y = localAbsY - scrollY;
      return { x, y };
    }

    // Then: document-relative normalization (stable across different viewport sizes)
    if (typeof pointer.docRelX === 'number' && typeof pointer.docRelY === 'number') {
      const doc = getDocumentSize();
      const localAbsX = pointer.docRelX * doc.width;
      const localAbsY = pointer.docRelY * doc.height;
      const x = localAbsX - scrollX;
      const y = localAbsY - scrollY;
      return { x, y };
    }

    // Fallback to absolute document coordinates when available
    if (typeof pointer.absX === 'number' && typeof pointer.absY === 'number') {
      const x = pointer.absX - scrollX;
      const y = pointer.absY - scrollY;
      return { x, y };
    }

    // Fallback to viewport-relative mapping
    const { width, height } = getViewportSize();
    const x = pointer.relativeX * width;
    const y = pointer.relativeY * height;

    return { x, y };
  }, [getViewportSize, getDocumentSize, getScrollableExtents]);

  // Broadcast message with current relative position
  const broadcastMessage = useCallback((message: string) => {
    // Don't broadcast messages if cursors are hidden or disabled
    if (!enabled || !cursorsVisible || !pageId || !currentUser) return;

    const user = activeUsers[currentUser.id];
    const presenceChannel = getPresenceChannel();

    if (presenceChannel) {
      const { relativeX, relativeY } = currentRelativePosition.current;
      const { absX, absY } = currentAbsolutePosition.current;
      const { docRelX, docRelY } = currentDocRelativePosition.current;
      const { scrollRelX, scrollRelY } = currentScrollRelative.current;
      const anchor = currentAnchorRef.current;

      presenceChannel.whisper('message', {
        userId: user.id,
        message: message.trim(),
        relativeX,
        relativeY,
        absX,
        absY,
        docRelX,
        docRelY,
        scrollRelX,
        scrollRelY,
        anchor,
        user,
        timestamp: Date.now()
      });
    }
  }, [enabled, cursorsVisible, pageId, currentUser, activeUsers, getPresenceChannel]);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === '/' && !showTempMessage) {
      event.preventDefault();
      dispatch(setShowTempMessage(true));
      dispatch(setInputValue(''));
      return;
    }

    if (event.key === 'Escape' && showTempMessage) {
      event.preventDefault();
      dispatch(setShowTempMessage(false));
      dispatch(setInputValue(''));

      if (hideMessageTimeoutRef.current) {
        clearTimeout(hideMessageTimeoutRef.current);
        hideMessageTimeoutRef.current = null;
      }
      return;
    }
  }, [showTempMessage, dispatch]);

  const handleEscapeMessage = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape' && showTempMessage) {
      event.preventDefault();
      dispatch(setShowTempMessage(false));
      dispatch(setInputValue(''));

      const presenceChannel = getPresenceChannel();
      if (presenceChannel && currentUser) {
        presenceChannel.whisper('cancel-message', {
          userId: currentUser.id,
          timestamp: Date.now()
        });
      }

      if (!enabled || !currentUser) return;
      setUserPointers(prev => {
        const newPointers = new Map(prev);
        const pointer = newPointers.get(currentUser.id);
        if (pointer) {
          newPointers.set(currentUser.id, {
            ...pointer,
            message: undefined
          });
        }
        return newPointers;
      });

      if (hideMessageTimeoutRef.current) {
        clearTimeout(hideMessageTimeoutRef.current);
        hideMessageTimeoutRef.current = null;
      }
      return;
    }
  }, [showTempMessage, currentUser, enabled, dispatch, getPresenceChannel]);

  const cleanupTimeouts = useCallback(() => {
    if (hideMessageTimeoutRef.current) {
      clearTimeout(hideMessageTimeoutRef.current);
      hideMessageTimeoutRef.current = null;
    }
    if (clearMessageTimeoutRef.current) {
      clearTimeout(clearMessageTimeoutRef.current);
      clearMessageTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (showTempMessage) {
      broadcastMessage(inputValue.trim());

      if (hideMessageTimeoutRef.current) {
        clearTimeout(hideMessageTimeoutRef.current);
        hideMessageTimeoutRef.current = null;
      }
      hideMessageTimeoutRef.current = setTimeout(() => {
        dispatch(setShowTempMessage(false));
        dispatch(setInputValue(''));
      }, 3000);
      return;
    }
  }, [showTempMessage, inputValue, broadcastMessage, dispatch]);

  const handleUserLeave = useCallback(() => {
    dispatch(setShowTempMessage(false));
    dispatch(setInputValue(''));
    cleanupTimeouts();
    broadcastMouseLeave();
  }, [cleanupTimeouts, broadcastMouseLeave, dispatch]);

  useEffect(() => {
    if (Object.keys(activeUsers).length > 0) {
      setUserPointers(prev => {
        const newPointers = new Map(prev);
        newPointers.delete(currentUser.id);
        return newPointers;
      });
    }
  }, [activeUsers, currentUser]);

  // Setup presence channel listeners using centralized manager
  useEffect(() => {
    if (!enabled || !pageId || !currentUser || !shouldConnect || !window.Echo || !isLoggedIn) {
      return;
    }

    const presenceChannel = presenceService.register(channelName!, subscriberId);
    if (!presenceChannel) {
      console.error(`❌ [use-realtime-pointers] Failed to get presence channel for ${channelName}`);
      return;
    }

    presenceChannel.listenForWhisper('mouse-move', (data: MouseMoveWhisperData) => {
      // Only process incoming pointer data if cursors are visible
      if (!cursorsVisible) return;

      if (data.userId !== currentUser.id) {
        setUserPointers(prev => new Map(prev).set(data.userId, {
          relativeX: data.relativeX,
          relativeY: data.relativeY,
          absX: data.absX,
          absY: data.absY,
          docRelX: data.docRelX,
          docRelY: data.docRelY,
          scrollRelX: data.scrollRelX,
          scrollRelY: data.scrollRelY,
          anchor: data.anchor,
          user: data.user,
          timestamp: Date.now(),
          message: data.message || ''
        }));
      }
    });

    presenceChannel.listenForWhisper('mouse-leave', (data: MouseLeaveWhisperData) => {
      if (data.userId !== currentUser.id) {
        setUserPointers(prev => {
          const newPointers = new Map(prev);
          newPointers.delete(data.userId);
          return newPointers;
        });
      }
    });

    presenceChannel.listenForWhisper('message', (data: MessageWhisperData) => {
      // Only process incoming messages if cursors are visible
      if (!cursorsVisible) return;

      if (data.userId !== currentUser.id) {
        setUserPointers(prev => {
          const newPointers = new Map(prev);

          newPointers.set(data.userId, {
            relativeX: data.relativeX,
            relativeY: data.relativeY,
            absX: data.absX,
            absY: data.absY,
            docRelX: data.docRelX,
            docRelY: data.docRelY,
            scrollRelX: data.scrollRelX,
            scrollRelY: data.scrollRelY,
            anchor: data.anchor,
            user: data.user,
            timestamp: data.timestamp,
            message: data.message
          });

          // Auto-clear message after 3 seconds
          setTimeout(() => {
            setUserPointers(current => {
              const updated = new Map(current);
              const pointer = updated.get(data.userId);
              if (pointer && pointer.timestamp === data.timestamp) {
                updated.set(data.userId, {
                  ...pointer,
                  message: undefined
                });
              }
              return updated;
            });
          }, 3000);

          return newPointers;
        });
      }
    });

    presenceChannel.listenForWhisper('cancel-message', (data: CancelMessageWhisperData) => {
      if (data.userId !== currentUser.id) {
        setUserPointers(prev => {
          const newPointers = new Map(prev);
          const pointer = newPointers.get(data.userId);
          if (pointer) {
            newPointers.set(data.userId, {
              ...pointer,
              message: undefined
            });
          }
          return newPointers;
        });
      }
    });

    return () => {
      if (presenceChannel) {
        try {
          presenceChannel.stopListeningForWhisper('mouse-move');
          presenceChannel.stopListeningForWhisper('mouse-leave');
          presenceChannel.stopListeningForWhisper('message');
          presenceChannel.stopListeningForWhisper('cancel-message');
        } catch (_e) { /* noop */ }
      }
      throttledBroadcastMouseMove.cancel();
      cleanupTimeouts();
    };
  }, [dispatch, currentUser, pageId, enabled, cursorsVisible, throttledBroadcastMouseMove, activeUsers, cleanupTimeouts, inputValue, shouldConnect, channelName, subscriberId, isLoggedIn, hasLeftPage, getPresenceChannel]);

  // Cleanup subscriber on unmount
  useEffect(() => {
    return () => {
      if (channelName) presenceService.unregister(channelName, subscriberId);
    };
  }, [channelName, subscriberId]);

  // Auto-cleanup stale pointers
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const now = Date.now();
      const staleThreshold = 5000;

      setUserPointers(prev => {
        const newPointers = new Map(prev);
        let hasChanges = false;

        for (const [userId, pointer] of prev) {
          if (now - pointer.timestamp > staleThreshold) {
            newPointers.delete(userId);
            hasChanges = true;
          }
        }

        return hasChanges ? newPointers : prev;
      });
    }, 2000);

    return () => clearInterval(cleanupInterval);
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        handleUserLeave();
      }
    };

    const handleBeforeUnload = () => {
      handleUserLeave();
    };

    const handleBlur = () => {
      handleUserLeave();
    };

    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0 || e.clientX <= 0 ||
        e.clientX >= window.innerWidth ||
        e.clientY >= window.innerHeight) {
        handleUserLeave();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('blur', handleBlur);
    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mouseleave', handleMouseLeave);
      throttledBroadcastMouseMove.cancel();
      cleanupTimeouts();
    };
  }, [handleKeyDown, handleUserLeave, handleMouseMove, cleanupTimeouts, enabled, throttledBroadcastMouseMove]);

  // Convert relative pointers to absolute coordinates for current viewport
  // Return empty array if cursors are hidden
  const pointers: PointerData[] = cursorsVisible
    ? Array.from(userPointers.values()).map(pointer => {
      const { x, y } = calculateLocalPosition(pointer);
      return {
        x,
        y,
        title: pointer.user?.first_name || pointer.user?.last_name || 'Unknown User',
        src: pointer.user?.avatar || null,
        userId: pointer.user?.id || '',
        message: pointer.message || ''
      };
    })
    : [];

  return {
    pointers,
    handleMouseMove,
    handleMouseLeave: broadcastMouseLeave,
    handleMouseEnter,
    handleEscapeMessage
  };
};